'use client';

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Loader2, Phone, ShieldCheck, X } from 'lucide-react';
import { bindPhone, ApiError, getStoredUser, setStoredUser } from '@/lib/api';
import { useAuthForm } from '@/lib/hooks/use-auth-form';
import { cn } from '@/lib/utils';
import { Z } from '@/lib/z-index';
import { SlideCaptcha } from '@/src/components/ui/slide-captcha';

const PHONE_FORMAT_RE = /^(\d{3})(\d{4})(\d{4})$/;

interface BindPhoneModalProps {
	onClose: () => void;
	onSuccess: (phone: string) => void;
	/** "default" = 个人中心弹窗，"workplace" = 工作台强制绑定（无关闭按钮，不同提示文案） */
	variant?: 'default' | 'workplace';
}

export function BindPhoneModal({ onClose, onSuccess, variant = 'default' }: BindPhoneModalProps) {
	const { otp, form } = useAuthForm();
	const [phone, setPhone] = useState('');
	const [step, setStep] = useState<1 | 2>(1);
	const { loading, error } = form;

	const submitBind = async (code: string) => {
		const trimmed = phone.trim();
		if (!trimmed) return;
		form.setLoading(true);
		form.setError('');
		try {
			await bindPhone(trimmed, code);
			const stored = getStoredUser();
			if (stored) setStoredUser({ ...stored, phone: trimmed });
			onSuccess(trimmed);
		} catch (err) {
			form.setError(err instanceof ApiError ? err.statusText || '绑定失败，请重试' : '网络异常，请稍后重试');
			otp.resetDigits();
		} finally {
			form.setLoading(false);
		}
	};

	const slideCaptchaOverlay = otp.slideCaptcha
		? createPortal(
				<div
					className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-black/40 p-4 animate-in fade-in duration-200"
					style={{ zIndex: Z.MODAL_NESTED }}
				>
					<div className="relative w-full max-w-[460px] bg-background rounded-2xl shadow-xl p-5 animate-in zoom-in-95 duration-200">
						<button
							className="absolute top-3 right-3 p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
							onClick={otp.cancelSlideCaptcha}
							type="button"
						>
							<X className="h-4 w-4" />
						</button>
						<p className="text-sm font-medium text-foreground mb-4 pr-6">请完成安全验证</p>
						<SlideCaptcha
							captcha={otp.slideCaptcha}
							disabled={otp.smsLoading}
							onRefresh={otp.refreshSlideCaptcha}
							onVerify={otp.handleSlideVerify}
							status={otp.slideCaptchaStatus}
						/>
					</div>
				</div>,
				document.body,
			)
		: null;

	return createPortal(
		<div
			className={cn(
				'fixed inset-0 flex items-center justify-center overflow-y-auto p-4 animate-in fade-in duration-200',
				variant === 'workplace' && 'pointer-events-none',
				variant !== 'workplace' && 'bg-black/45',
			)}
			style={{ zIndex: Z.MODAL_OVERLAY }}
		>
			<div className="relative w-full max-w-[430px] rounded-2xl border border-border bg-background p-5 shadow-2xl animate-in zoom-in-95 pointer-events-auto duration-200">
				{variant === 'default' && (
					<button
						className="absolute right-3 top-3 rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
						onClick={onClose}
						type="button"
					>
						<X className="h-4 w-4" />
					</button>
				)}

				<div className={cn('mb-5 flex items-center gap-3', variant === 'default' && 'pr-8')}>
					<div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
						<ShieldCheck className="h-5 w-5" />
					</div>
					<div>
						<h2 className="text-base font-semibold text-foreground">绑定手机号</h2>
						<p className="mt-0.5 text-xs text-muted-foreground">
							{variant === 'workplace' ? '绑定手机号后即可使用工作台，还可用于验证码登录和账号安全验证' : '绑定后可用于验证码登录和账号安全验证'}
						</p>
					</div>
				</div>

				{step === 1 ? (
					<div className="space-y-4">
						<label className="block space-y-2">
							<span className="pl-1 text-xs font-medium tracking-wide text-muted-foreground">手机号码</span>
							<div className="relative">
								<Phone className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<input
									autoComplete="tel"
									className={cn(
										'h-10 w-full rounded-xl border bg-background pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground/60 transition-all focus:outline-none focus:ring-2 focus:ring-primary/30',
										error ? 'border-destructive/50' : 'border-border focus:border-primary/50',
									)}
									onChange={(e) => {
										setPhone(e.target.value);
										form.setError('');
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault();
											void otp.handlePhoneNext(phone, 'bind', () => setStep(2));
										}
									}}
									placeholder="输入手机号"
									type="tel"
									value={phone}
								/>
							</div>
						</label>

						{error && (
							<div className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/5 px-3.5 py-2.5">
								<AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
								<p className="text-xs text-destructive">{error}</p>
							</div>
						)}

						<button
							className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
							disabled={otp.smsLoading || otp.slideCaptchaLoading || !phone.trim()}
							onClick={() => void otp.handlePhoneNext(phone, 'bind', () => setStep(2))}
							type="button"
						>
							{otp.smsLoading || otp.slideCaptchaLoading ? (
								<>
									<Loader2 className="h-4 w-4 animate-spin" />
									加载中...
								</>
							) : (
								'发送验证码'
							)}
						</button>
					</div>
				) : (
					<div className="space-y-3">
						<div className="flex items-center justify-center gap-2">
							<span className="text-sm font-medium tracking-wide text-foreground">{phone.trim().replace(PHONE_FORMAT_RE, '$1 $2 $3')}</span>
							<button
								className="text-xs text-primary hover:underline"
								onClick={() => {
									setStep(1);
									form.setError('');
									otp.resetDigits();
								}}
								type="button"
							>
								更换
							</button>
						</div>
						<p className="mb-5 text-center text-xs text-muted-foreground">验证码已发送至该手机号</p>

						<div className="mb-4 flex justify-center gap-2.5" onPaste={(e) => otp.handleDigitPaste(e, submitBind)}>
							{otp.codeDigits.map((digit, i) => (
								<input
									className={cn(
										'h-14 w-12 rounded-xl border-2 bg-background text-center text-2xl font-semibold transition-all focus:outline-none',
										loading && 'opacity-50',
										error
											? 'border-destructive/50 focus:border-destructive'
											: digit
												? 'border-primary/50 focus:border-primary'
												: 'border-border focus:border-primary/50',
									)}
									disabled={loading}
									inputMode="numeric"
									key={i}
									maxLength={1}
									onChange={(e) => otp.handleDigitChange(i, e.target.value, submitBind)}
									onKeyDown={(e) => otp.handleDigitKeyDown(i, e)}
									ref={(el) => {
										otp.digitRefs.current[i] = el;
									}}
									type="text"
									value={digit}
								/>
							))}
						</div>

						{loading && (
							<div className="flex items-center justify-center gap-2 py-2">
								<Loader2 className="h-4 w-4 animate-spin text-primary" />
								<span className="text-sm text-muted-foreground">绑定中...</span>
							</div>
						)}
						{error && <p className="mb-2 text-center text-xs text-destructive">{error}</p>}

						<div className="text-center">
							<button
								className="text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
								disabled={otp.smsCooldown > 0 || otp.smsLoading || otp.slideCaptchaLoading}
								onClick={() => void otp.handleSendSms(phone, 'bind')}
								type="button"
							>
								{otp.smsCooldown > 0 ? `${otp.smsCooldown}s 后可重新发送` : '重新发送验证码'}
							</button>
						</div>

						<div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
							<CheckCircle2 className="h-3.5 w-3.5" />
							输入 6 位验证码后自动完成绑定
						</div>
					</div>
				)}
			</div>
			{slideCaptchaOverlay}
		</div>,
		document.body,
	);
}
